import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const SelectorContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1000;
`;

const SelectorButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    border-color: rgba(255, 68, 68, 0.3);
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: rgba(20, 20, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(20px);
`;

const LanguageOption = styled(motion.button)<{ $isActive: boolean }>`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.$isActive ? 'rgba(255, 68, 68, 0.15)' : 'transparent'};
  border: none;
  color: ${props => props.$isActive ? '#ff4444' : '#fff'};
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    color: #ff4444;
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const FlagIcon = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const LanguageName = styled.span`
  flex: 1;
`;

const ActiveIndicator = styled.span`
  width: 6px;
  height: 6px;
  background: #ff4444;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(255, 68, 68, 0.5);
`;

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'pt-BR', name: 'Português (BR)', flag: '🇧🇷' },
  { code: 'en', name: 'English', flag: '🇺🇸' }
];

const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang =>
    lang.code === i18n.language || i18n.language.startsWith(lang.code)
  ) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);

    // Salvar explicitamente no localStorage
    localStorage.setItem('i18nextLng', languageCode);

    // Update document language
    document.documentElement.lang = languageCode;

    // Update meta tags for SEO
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaOgDescription = document.querySelector('meta[property="og:description"]');

    if (languageCode === 'en') {
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Complete AI-powered CRM platform to automate your customer service and boost your sales.');
      }
      if (metaOgDescription) {
        metaOgDescription.setAttribute('content', 'Complete AI-powered CRM platform to automate your customer service and boost your sales.');
      }
    } else {
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Plataforma completa de CRM com IA para automatizar seu atendimento e potencializar suas vendas.');
      }
      if (metaOgDescription) {
        metaOgDescription.setAttribute('content', 'Plataforma completa de CRM com IA para automatizar seu atendimento e potencializar suas vendas.');
      }
    }

    // Título sempre "Loopra" (consistente em todos idiomas)
    document.title = 'Loopra';
  };

  return (
    <SelectorContainer>
      <SelectorButton
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <span>{currentLanguage.flag}</span>
        <span>{currentLanguage.code.split('-')[0].toUpperCase()}</span>
      </SelectorButton>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999
              }}
            />
            <DropdownMenu
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {languages.map((language) => (
                <LanguageOption
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  $isActive={currentLanguage.code === language.code}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FlagIcon>{language.flag}</FlagIcon>
                  <LanguageName>{language.name}</LanguageName>
                  {currentLanguage.code === language.code && <ActiveIndicator />}
                </LanguageOption>
              ))}
            </DropdownMenu>
          </>
        )}
      </AnimatePresence>
    </SelectorContainer>
  );
};

export default LanguageSelector;
